/********************************************************************
 *  Copyright 2025 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  Adobe permits you to use, modify, and distribute this
 * file in accordance with the terms of the Adobe license agreement
 * accompanying it.
 *******************************************************************/

import { fetchGraphQl } from '@/quote-management/api';
import { SEND_QUOTE_TEMPLATE_FOR_REVIEW_MUTATION } from './graphql/sendQuoteTemplateForReview';
import { transformQuoteTemplate } from '@/quote-management/data/transforms';
import { state } from '@/quote-management/lib/state';
import { events } from '@adobe-commerce/event-bus';

export interface ReferenceDocumentLinkInput {
  uid?: string;
  name: string;
  identifier?: string;
  url: string;
}

export interface SendQuoteTemplateForReviewParams {
  templateId: string;
  name?: string;
  comment?: string;
  referenceDocumentLinks?: ReferenceDocumentLinkInput[];
  attachments?: { key: string }[];
}

export const sendQuoteTemplateForReview = async (
  params: SendQuoteTemplateForReviewParams
) => {
  if (!params.templateId) {
    throw new Error('Template ID is required');
  }

  if (!state.authenticated) {
    throw new Error('Unauthorized');
  }

  try {
    // Transform reference document links from camelCase to snake_case for GraphQL
    const referenceDocumentLinks = params.referenceDocumentLinks?.map((link) => ({
      link_id: link.uid,
      document_name: link.name,
      document_identifier: link.identifier,
      reference_document_url: link.url,
    }));

    // Format attachments for GraphQL (separate field, not nested in comment)
    const attachments = params.attachments?.length 
      ? params.attachments.map(att => ({ key: att.key }))
      : undefined;

    const results: any = await fetchGraphQl(
      SEND_QUOTE_TEMPLATE_FOR_REVIEW_MUTATION,
      {
        variables: {
          templateId: params.templateId,
          name: params.name,
          comment: params.comment || undefined,
          referenceDocumentLinks,
          attachments,
        },
      }
    );

    if (!results?.data?.submitNegotiableQuoteTemplateForReview) {
      throw new Error('No quote template data received');
    }

    const transformedData = transformQuoteTemplate(
      results.data.submitNegotiableQuoteTemplateForReview
    );

    if (!transformedData) {
      throw new Error('Failed to transform quote template data');
    }

    events.emit('quote-management/quote-template-data', {
      quoteTemplate: transformedData,
      permissions: state.permissions,
    });

    return transformedData;
  } catch (error) {
    return Promise.reject(error);
  }
};
